import { Component } from '@theme/component'
import { loadGsapPlugins } from '@theme/gsap'

const mod = (number, base) => ((number % base) + base) % base

/**
 * @extends {Component<{
 *   list: HTMLElement,
 *   dragger: HTMLElement,
 * }>}
 */
class FlickCardsComponent extends Component {
  requiredRefs = ['list', 'dragger']

  #activeIndex = 0
  #cards = []
  #videos = []
  #draggable = null
  #gsap = null
  #keyboardAbortController = null
  #prefersReducedMotion = globalThis.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches

  async mounted() {
    this.#cards = Array.from(
      this.refs.list.querySelectorAll('[data-flick-cards-item]')
    )

    if (this.#cards.length < 3) return

    this.#setupKeyboard()
    this.#setupVideos()
    this.#gsap = window.gsap
    this.#renderDiscrete(this.#activeIndex)
    this.#syncVideoPlayback()

    const gsap = await loadGsapPlugins('Draggable')
    if (!(this.isConnected && gsap && globalThis.Draggable)) {
      return
    }

    this.#gsap = gsap
    this.#setupDraggable()
  }

  beforeUnmount() {
    this.#keyboardAbortController?.abort()
    this.#draggable?.kill()

    for (const video of this.#videos) {
      this.#pauseVideo(video)
    }
  }

  selectCard(card) {
    const index = this.#cards.indexOf(card)
    if (index === -1) return
    this.#goTo(index - this.#activeIndex)
  }

  #setupKeyboard() {
    this.#keyboardAbortController = new AbortController()
    this.setAttribute('tabindex', this.getAttribute('tabindex') ?? '0')
    this.addEventListener('keydown', this.#handleKeydown, {
      signal: this.#keyboardAbortController.signal
    })
  }

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.#goTo(-1)
      return
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.#goTo(1)
    }
  }

  #setupVideos() {
    this.#videos = this.#cards.map((card) => {
      const video = card.querySelector(
        '[data-flick-cards-video] :is(ui-video, ui-video-media)'
      )
      return video instanceof HTMLElement ? video : null
    })
  }

  #setupDraggable() {
    const deckWidth = this.clientWidth
    const component = this

    this.#draggable = globalThis.Draggable.create(this.refs.dragger, {
      activeCursor: 'inherit',
      bounds: { maxX: deckWidth / 2, minX: -deckWidth / 2 },
      cursor: 'inherit',
      edgeResistance: 0.8,
      inertia: false,

      onDrag() {
        component.#renderDragProgress(this.x / deckWidth)
      },

      onPress() {
        component.setAttribute('data-flick-drag-status', 'grabbing')
      },

      onRelease() {
        const raw = this.x / deckWidth
        let shift = 0

        if (raw > 0.1) shift = -1
        if (raw < -0.1) shift = 1

        component.#goTo(shift)
        component.#gsap.to(this.target, {
          duration: component.#prefersReducedMotion ? 0 : 0.3,
          ease: 'power1.out',
          x: 0
        })
        component.setAttribute('data-flick-drag-status', 'grab')
      },
      type: 'x'
    })[0]
  }

  #renderDragProgress(rawProgress) {
    const progress = Math.min(1, Math.abs(rawProgress))
    const direction = rawProgress > 0 ? -1 : 1
    const nextIndex = mod(this.#activeIndex + direction, this.#cards.length)

    for (const [index, card] of this.#cards.entries()) {
      const current = this.#getConfig(index, this.#activeIndex)
      const next = this.#getConfig(index, nextIndex)
      const mix = (property) =>
        current[property] + (next[property] - current[property]) * progress

      this.#gsap.set(card, {
        opacity: mix('opacity'),
        rotation: mix('rotation'),
        scale: mix('scale'),
        xPercent: mix('x'),
        yPercent: mix('y')
      })
    }
  }

  #goTo(shift) {
    const previousIndex = this.#activeIndex
    this.#activeIndex = mod(this.#activeIndex + shift, this.#cards.length)
    this.#renderDiscrete(this.#activeIndex)
    this.#syncVideoPlayback(previousIndex)
  }

  #syncVideoPlayback(previousIndex = null) {
    const activeVideo = this.#videos[this.#activeIndex]

    for (const [index, video] of this.#videos.entries()) {
      if (index === this.#activeIndex) continue
      if (previousIndex === null || index === previousIndex) {
        this.#pauseVideo(video)
      }
    }

    if (this.#prefersReducedMotion) return
    this.#playVideo(activeVideo)
  }

  #playVideo(video) {
    if (!video || typeof video.play !== 'function') return
    video.play()
  }

  #pauseVideo(video) {
    if (!video || typeof video.pause !== 'function') return
    video.pause()
  }

  #renderDiscrete(activeIndex) {
    for (const [index, card] of this.#cards.entries()) {
      const config = this.#getConfig(index, activeIndex)
      const status = this.#getStatus(config)

      card.setAttribute('data-flick-cards-item-status', status)
      card.style.zIndex = config.z

      if (!this.#gsap) {
        card.style.opacity = String(config.opacity)
        card.style.transform = `translate(${config.x}%, ${config.y}%) rotate(${config.rotation}deg) scale(${config.scale})`
        continue
      }

      this.#gsap.to(card, {
        duration: this.#prefersReducedMotion ? 0 : 0.6,
        ease: 'elastic.out(1.2,1)',
        opacity: config.opacity,
        rotation: config.rotation,
        scale: config.scale,
        xPercent: config.x,
        yPercent: config.y
      })
    }
  }

  #getConfig(index, activeIndex) {
    const total = this.#cards.length
    let difference = index - activeIndex

    if (difference > total / 2) difference -= total
    if (difference < -total / 2) difference += total

    switch (difference) {
      case 0:
        return { opacity: 1, rotation: 0, scale: 1, x: 0, y: 0, z: 5 }
      case 1:
        return { opacity: 1, rotation: 10, scale: 0.9, x: 25, y: 1, z: 4 }
      case -1:
        return { opacity: 1, rotation: -10, scale: 0.9, x: -25, y: 1, z: 4 }
      case 2:
        return { opacity: 1, rotation: 15, scale: 0.8, x: 45, y: 5, z: 3 }
      case -2:
        return { opacity: 1, rotation: -15, scale: 0.8, x: -45, y: 5, z: 3 }
      default: {
        const direction = difference > 0 ? 1 : -1
        return {
          opacity: 0,
          rotation: 20 * direction,
          scale: 0.6,
          x: 55 * direction,
          y: 5,
          z: 2
        }
      }
    }
  }

  #getStatus(config) {
    if (config.x === 0) return 'active'
    if (config.x === 25) return '2-after'
    if (config.x === -25) return '2-before'
    if (config.x === 45) return '3-after'
    if (config.x === -45) return '3-before'
    return 'hidden'
  }
}

customElements.define('flick-cards-component', FlickCardsComponent)
